/**
 * Canonical tenant role vocabulary — matches the backend enum from GET /api/v1/roles.
 *
 * Single source of truth for the frontend. Import from here instead of
 * duplicating ROLE_LABELS in individual files.
 */

/** All tenant roles the backend recognises. */
export type TenantRole =
  | 'admin'
  | 'office_staff'
  | 'supervisor'
  | 'field_worker'
  | 'logistics'
  | 'dispatcher'
  | 'sales'
  | 'customer';

/**
 * Human-readable labels keyed by TenantRole.
 *
 * `Record<TenantRole, string>` (not Partial) on purpose: adding a value to
 * TenantRole must fail the build here rather than silently rendering a
 * fallback label somewhere in the UI.
 */
export const ROLE_LABELS: Record<TenantRole, string> = {
  admin: 'Admin',
  office_staff: 'Office Staff',
  supervisor: 'Supervisor',
  field_worker: 'Field Worker',
  logistics: 'Logistics',
  dispatcher: 'Dispatcher',
  sales: 'Sales',
  customer: 'Customer',
};

/**
 * Compact labels for the narrow ACCESS column on the team list, which has room
 * for one word. Same exhaustiveness guarantee as ROLE_LABELS — a new TenantRole
 * value fails the build here too.
 */
export const ROLE_ACCESS_LABELS: Record<TenantRole, string> = {
  admin: 'Admin',
  office_staff: 'Office',
  supervisor: 'Supervisor',
  field_worker: 'Field',
  logistics: 'Logistics',
  dispatcher: 'Dispatcher',
  sales: 'Sales',
  customer: 'Customer',
};

/**
 * Every tenant role, in display order. Derived from ROLE_LABELS so a new role
 * shows up in every dropdown automatically.
 */
export const ALL_TENANT_ROLES = Object.keys(ROLE_LABELS) as TenantRole[];

/**
 * Roles available in the "Add & Invite" modal.
 * Admin is intentionally excluded — admin accounts are created through
 * other flows. Customer is a portal role, not invite-assignable.
 */
export const INVITABLE_ROLES: TenantRole[] = [
  'field_worker',
  'logistics',
  'dispatcher',
  'office_staff',
  'supervisor',
  // Read-only on customers, contacts, quotes, jobs and tickets, plus their own
  // expenses — see lib/sales-access.ts and the API's sales-role-guard.ts.
  'sales',
];

/**
 * Is this role one of the tenant's own people — the API's `INTERNAL_ROLES`
 * (fieldiq-api src/lib/internal-roles.ts), the set `requireInternalUser`
 * admits on /sync and every other internal-only door.
 *
 * An ALLOWLIST on purpose, matching the API. `role !== 'customer'` is the same
 * predicate today and fails open tomorrow: a label added later for another
 * outside party would be treated as staff here while the API refuses it —
 * the shape api#809 flipped on the download doors. Keyed on every TenantRole
 * so adding one fails the build until someone says which side it is on. A role
 * this file has never heard of (including null/undefined) is NOT internal.
 */
const INTERNAL_ROLE: Record<TenantRole, boolean> = {
  admin: true,
  office_staff: true,
  supervisor: true,
  field_worker: true,
  logistics: true,
  dispatcher: true,
  // An employee, as in the API's INTERNAL_ROLES (api#1032). The API's sales
  // guard still refuses every internal-only door not on its allowlist.
  sales: true,
  customer: false,
};

export function isInternalRole(role: string | undefined | null): boolean {
  if (!role || !Object.prototype.hasOwnProperty.call(INTERNAL_ROLE, role)) return false;
  return INTERNAL_ROLE[role as TenantRole];
}

/** Look up a role label with a fallback to the raw value. */
export function getRoleLabel(role: string): string {
  return (ROLE_LABELS as Record<string, string>)[role] || role;
}

/**
 * Look up the compact ACCESS-column label for a role.
 * Unknown values fall back to the raw role string — never to another role's
 * label, which is what made a dispatcher render as "Field".
 */
export function getRoleAccessLabel(role: string): string {
  return (ROLE_ACCESS_LABELS as Record<string, string>)[role] || role;
}

/**
 * Check whether a user role has admin-level access.
 * Replaces scattered `role === 'admin' || role === 'owner'` checks.
 * 'owner' is a platform role that should not appear in tenant data,
 * but we still accept it defensively during migration.
 */
export function isAdminRole(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'owner';
}

/**
 * Check whether a user role has admin or supervisor access.
 * Replaces `role === 'admin' || role === 'supervisor' || role === 'owner'`.
 */
export function isAdminOrSupervisor(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'supervisor' || role === 'owner';
}

/**
 * Check whether a user role can approve tickets (office approve, send to
 * customer). Matches the API's requireRole('admin', 'office_staff',
 * 'supervisor') on POST /tickets/:id/approve.
 */
export function canApproveTickets(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'supervisor' || role === 'owner';
}

/**
 * Check whether a user role can field-approve a submitted ticket.
 * Field workers, supervisors, and admins may field-approve.
 */
export function canFieldApprove(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'field_worker' || role === 'supervisor' || role === 'owner';
}

/**
 * Form library gates.
 *
 * These three deliberately do NOT go through `isAdminRole`. That helper also
 * accepts the legacy platform role `owner`, and the API's own checks are
 * literal string comparisons against the JWT role — so an `owner` would be
 * shown a control that answers 403. A button that cannot succeed is worse than
 * a missing one: the user reports it as broken, and the real answer is that it
 * was never theirs.
 *
 * Mirrors, exactly:
 *   GET /forms/templates?include_drafts=1  LIBRARY_MANAGER_ROLES = {admin, dispatcher}
 *   PUT /forms/library                     requireRole('admin', 'dispatcher')
 *   POST /forms/templates/:id/publish      requireRole('admin', { strict: true })
 *   POST /forms/source-documents           requireRole('admin', 'office_staff')
 *   POST /forms/extractions                requireRole('admin', 'office_staff')
 */

/** Curate the library: see drafts, toggle Enabled, move Default. */
export function canManageFormLibrary(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'dispatcher';
}

/**
 * Publish a draft template.
 *
 * `{ strict: true }` on the route opts OUT of dispatcher admin-elevation, so
 * this is the literal `admin` role and nothing else — an elevated dispatcher
 * who may curate the library still may not put a new document in front of a
 * crew.
 */
export function canPublishFormTemplate(role: string | undefined | null): boolean {
  return role === 'admin';
}

/**
 * Upload a source document and run an extraction.
 *
 * Office staff CAN do this — it is not an admin-only flow. A dispatcher whose
 * company has `dispatcher_admin_access` enabled would also pass the server's
 * non-strict check, but that flag is not on the auth payload, so it cannot be
 * evaluated here. Gating on the literal allow list under-grants an elevated
 * dispatcher rather than showing anyone a button that 403s.
 */
export function canUploadSourceForm(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff';
}

/**
 * Attach a customer-signed copy of a ticket (api#995).
 *
 * Mirrors, exactly:
 *   POST /tickets/:id/customer-signed-copy  requireRole('admin', 'office_staff', { strict: true })
 *
 * Strict, so no dispatcher elevation and no `owner` / `supervisor` — narrower
 * than `canApproveTickets`, which is why it is its own function.
 */
export function canAttachCustomerSignedCopy(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff';
}

/**
 * Declare that a worker is not paid from jobs, or take the declaration back.
 *
 * Mirrors, exactly:
 *   GET /payroll/workers/:userId/job-pay-exemption  requireRole('admin', { strict: true })
 *   PUT /payroll/workers/:userId/job-pay-exemption  requireRole('admin', { strict: true })
 *
 * `{ strict: true }` opts OUT of dispatcher admin-elevation, so this is the
 * literal `admin` role and nothing else — the same reasoning as
 * `canPublishFormTemplate`, and for a bigger reason. Without `strict`, whether a
 * dispatcher could declare a colleague unpaid would depend on the tenant's
 * `dispatcher_admin_access` flag, which is not on the auth payload and could not
 * be evaluated here anyway. A per-tenant accident is not an access rule.
 *
 * Deliberately NOT `isAdminRole`: that helper also accepts the legacy platform
 * role `owner`, which the API's literal `=== 'admin'` comparison rejects, so an
 * owner would be shown a control that answers 403.
 *
 * READING the value is not gated on this. `jobPayExemption` arrives on
 * `safeUser` for any authenticated user of the same company, which is how the
 * employee profile renders it. The strict gate is on the payroll endpoints, and
 * the API's own comment is explicit that this is not a secrecy claim — so the
 * SETTER is what hides, never the sentence.
 */
export function canSetJobPayExemption(role: string | undefined | null): boolean {
  return role === 'admin';
}

/**
 * Whether a user may mark a pay period paid.
 *
 * Mirrors, exactly:
 *   POST /pay-periods/:id/mark-paid   requireRole('admin', { strict: true })
 *
 * Same reasoning as `canSetJobPayExemption`, and deliberately NOT
 * `isAdminRole`: that helper also accepts the legacy platform role `owner`,
 * which the API's literal `=== 'admin'` comparison rejects, so an owner would
 * be shown a control that answers 403. `{ strict: true }` also opts out of
 * dispatcher admin-elevation, which depends on the tenant's
 * `dispatcher_admin_access` flag — not on the auth payload, so it could not be
 * evaluated here even if we wanted to.
 *
 * This gate hides the control. It is not the enforcement — the API is — and it
 * is not a secrecy claim either: the period's status is visible to anyone who
 * can see the screen. Hiding the button only keeps someone from firing a
 * one-way transition they are not allowed to make.
 */
export function canMarkPayPeriodPaid(role: string | undefined | null): boolean {
  return role === 'admin';
}

/**
 * Whether a user may reopen an approved pay period.
 *
 * Mirrors `requireRole('admin', { strict: true })` on
 * `POST /pay-periods/:id/reopen`. A separate function from
 * `canMarkPayPeriodPaid` even though the body is identical today: they are two
 * API gates that happen to agree, and one moving must not silently move the
 * other. Same caveats — not `isAdminRole` (owner would see a 403), and strict
 * opts out of dispatcher elevation.
 */
export function canReopenPayPeriod(role: string | undefined | null): boolean {
  return role === 'admin';
}

/**
 * Whether a user is OFFERED the office correction on a timesheet.
 *
 * Mirrors `requireRole('admin', { strict: true })` on
 * `POST /timesheets/:id/admin-edit`. Its own function for the reason
 * `canReopenPayPeriod` is: a separate API gate that happens to agree today.
 *
 * Role only. The service's other refusals — the admin's own shift, a locked
 * row, a closed period — are relational and are left to the API to state.
 */
export function canAdminEditTimesheet(role: string | undefined | null): boolean {
  return role === 'admin';
}

/**
 * Whether a user may change a CAODC certification counter or cycle start date.
 *
 * Mirrors, exactly:
 *   CERT_FIELD_WRITE_ROLES in the API's src/lib/cert-field-guard.ts —
 *   ['admin', 'office_staff', 'dispatcher'], enforced inside POST /equipment
 *   and PUT/PATCH /equipment/:id on the two columns
 *   `working_days_since_inspection` and `cert_start_date`.
 *
 * `dispatcher` is on the list OUTRIGHT, not through admin-elevation, so unlike
 * `canMarkPayPeriodPaid` there is no per-tenant flag this gate cannot see and
 * no risk of under-granting an elevated dispatcher. And deliberately NOT
 * `isAdminRole`: that helper accepts the legacy platform role `owner`, which
 * the API's list does not contain, so an owner would be shown an input whose
 * save answers 403.
 *
 * ── WHAT HIDING DOES AND DOES NOT DO ────────────────────────────────────────
 * The API gate fires on CHANGE, not on presence, so a hidden input whose value
 * is still sitting in form state and gets PUT back unchanged is a no-op, not a
 * 403. That is what makes hiding safe here: a field_worker editing `notes` on a
 * unit that has a counter still saves cleanly.
 *
 * This is not the enforcement and it is not a secrecy claim — the counter is
 * rendered on the detail card to everyone who can open the screen, which is
 * correct: a hand needs to know how many days are left on the tool they are
 * about to run. Only the ability to TYPE OVER it is withheld.
 */
export function canWriteCertFields(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'dispatcher';
}

/**
 * Whether a user may CLEAR a recorded phase start (wash or run) on a job.
 *
 * Mirrors DELETE /dispatch/jobs/:id/phase/:phase/start in the API's
 * dispatch.routes.ts — requireRole('admin', 'office_staff', 'dispatcher').
 * NOT JOB_WRITE_ROLES: a supervisor is in the field, and this rewrites what
 * the field recorded. Not `isAdminRole` either, which admits the legacy
 * `owner` the API's list does not.
 */
export function canClearPhaseStart(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'dispatcher';
}

/**
 * Whether a user may upload or remove an equipment inspection certificate PDF.
 *
 * Mirrors JOB_WRITE_ROLES in the API's src/lib/job-write-roles.ts —
 * ['admin', 'office_staff', 'supervisor', 'dispatcher'] — which gates both
 * doors since api#817: an `equipment_cert` presign answers 403 to any other
 * internal role, and so does DELETE /uploads/:id.
 *
 * NOT canWriteCertFields above, which is a different list: `supervisor` may
 * file the document but may not type over the counter. Keep them apart.
 *
 * field_worker and driver never reach the equipment screens (their route trees
 * have no /equipment), so the role this hides the controls from in practice is
 * `logistics`. Viewing the document stays open to everyone who can open the
 * screen.
 */
export function canManageEquipmentCertDocuments(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'supervisor' || role === 'dispatcher';
}

/**
 * Write a customer record or one of its contacts — add, edit, remove.
 *
 * Mirrors, exactly, the API's `CUSTOMER_WRITE_ROLES`
 * (fieldiq-api src/lib/customer-write-roles.ts) = admin, office_staff,
 * supervisor, dispatcher, which gates POST/PATCH/DELETE on
 * /customers/:customerId/contacts. Before this the contact buttons were drawn
 * for every role, and a field hand who pressed one got a 403 toast for a
 * control that was never theirs.
 *
 * Keyed on every TenantRole, like INTERNAL_ROLE above, so a new role fails the
 * build until someone says which side it is on. Not `isAdminRole`: that also
 * admits the legacy `owner`, which the API's list does not name. A role this
 * file has never heard of (including null/undefined) cannot write.
 */
const CUSTOMER_WRITE_ROLE: Record<TenantRole, boolean> = {
  admin: true,
  office_staff: true,
  supervisor: true,
  dispatcher: true,
  field_worker: false,
  logistics: false,
  // Read-only on customers and contacts (operator, 2026-10-11): the API keeps
  // CUSTOMER_WRITE_ROLES on these gates, not CRM_ROLES.
  sales: false,
  customer: false,
};

export function canWriteCustomers(role: string | undefined | null): boolean {
  if (!role || !Object.prototype.hasOwnProperty.call(CUSTOMER_WRITE_ROLE, role)) return false;
  return CUSTOMER_WRITE_ROLE[role as TenantRole];
}

/**
 * WHO USES THE CRM (sales-log D5). Mirrors, exactly, the API's `CRM_ROLES`
 * (fieldiq-api src/lib/crm-roles.ts) = CUSTOMER_WRITE_ROLES + `sales`.
 * `logistics` is excluded BY DECISION (D12, operator 2026-10-07).
 *
 * Read by the CRM screens (CRM-FE-1..4, e.g. hooks/useCustomerPermissions).
 * A new CRM page must also add its route to lib/sales-access.ts's
 * SALES_ROUTES, or a sales user is redirected away from it.
 */
export const CRM_ROLES: readonly TenantRole[] = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'sales'];

export function isCrmRole(role: string | undefined | null): boolean {
  return !!role && (CRM_ROLES as readonly string[]).includes(role);
}
